import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { FileText, Download, Eye, X } from './icons';
import api from '../services/api';

export default function GenerarPDFEvidencia({ evidencia, onCancelar }) {
    const [generando, setGenerando] = useState(false);
    const [error, setError] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);

    const generarPDF = async () => {
        setGenerando(true);
        setError(null);

        try {
            const response = await api.post(
                `/EvidenciaSeguridad/${evidencia.id}/generar-pdf`,
                null,
                { responseType: 'blob' },
            );

            const blob = response.data;

            // Verificar que el blob es un PDF válido
            if (blob.type !== 'application/pdf') {
                throw new Error('El archivo generado no es un PDF válido');
            }

            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (error) {
            console.error('Error al generar PDF:', error);
            setError(error.message || 'Error al generar el PDF');
        } finally {
            setGenerando(false);
        }
    };

    const descargarPDF = () => {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            // Usar el código de control como nombre del archivo, reemplazando puntos y espacios
            const fileName = `${evidencia.codigoControl.replace(/\./g, '_').replace(/\s+/g, '_')}.pdf`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const previsualizarPDF = () => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generar PDF de Evidencia</h3>
                    <button
                        onClick={onCancelar}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
            </div>

            <div className="p-6">
                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
                                <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Información de la evidencia */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">{evidencia.codigoControl}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{evidencia.descripcionControl}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>Prioridad: {evidencia.prioridad}</span>
                        <span>Tipo: {evidencia.tipo}</span>
                        <span>Estado: {evidencia.estado}</span>
                        <span>Imágenes: {evidencia.imagenes?.length || 0}</span>
                    </div>
                </div>

                {/* Contenido del PDF */}
                <div className="mb-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Contenido del PDF</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Información del control de seguridad</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Detalles de implementación</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Evidencias visuales ({evidencia.imagenes?.length || 0} imágenes)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Comentarios y observaciones</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Información de aprobación</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Metadatos y trazabilidad</span>
                        </div>
                    </div>
                </div>

                {/* Estado del PDF */}
                {!pdfUrl && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                                PDF no generado
                            </span>
                        </div>
                        <p className="mt-1 text-sm text-blue-700">
                            Haz clic en "Generar PDF" para crear el documento con toda la información de la evidencia.
                        </p>
                    </div>
                )}

                {pdfUrl && (
                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5 text-green-600" />
                            <span className="text-sm font-medium text-green-900">
                                PDF generado exitosamente
                            </span>
                        </div>
                        <p className="mt-1 text-sm text-green-700">
                            El PDF está listo para descargar o previsualizar.
                        </p>
                    </div>
                )}

                {/* Botones de acción */}
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onCancelar}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Cancelar
                    </button>

                    {!pdfUrl && (
                        <button
                            onClick={generarPDF}
                            disabled={generando}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {generando ? 'Generando...' : 'Generar PDF'}
                        </button>
                    )}

                    {pdfUrl && (
                        <>
                            <button
                                onClick={previsualizarPDF}
                                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <Eye className="h-4 w-4" />
                                <span>Previsualizar</span>
                            </button>
                            <button
                                onClick={descargarPDF}
                                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                <Download className="h-4 w-4" />
                                <span>Descargar</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
